import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FALHAS_ATE_AVISAR, usePareamento } from '@/comuns/usePareamento';
import type { CanalDeIntegracao, PareamentoDoCanal } from '@/data/tipos';

export interface PareamentoModalProps {
    canal: CanalDeIntegracao | null;
    /** O primeiro estado, quando a tela já pediu o QR ao abrir. */
    inicial?: PareamentoDoCanal | null;
    onFechar: () => void;
    /** Chamado quando o número entra, para a lista recarregar. */
    onPareado: () => void;
}

/**
 * O pareamento do WhatsApp: gere, leia, conecte.
 *
 * A conexão é **assíncrona e do outro lado**: quem decide se pareou é o celular
 * de alguém, e nada nesta tela sabe disso até perguntar. Daí o formato — um
 * pedido a cada três segundos, uma contagem que diz quando o código vence e um
 * botão para pedir outro. Uma imagem estática esperaria calada por um evento
 * que talvez nunca venha.
 */
export function PareamentoModal({ canal, inicial, onFechar, onPareado }: PareamentoModalProps) {
    const { estado, erro, gerando, restante, falhas, conectado, temQr, gerar } = usePareamento(canal?.id ?? null, inicial, onPareado);

    return (
        <Modal
            open={canal !== null}
            title={canal ? `Conectar ${canal.nome}` : 'Conectar'}
            onClose={onFechar}
            width={520}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>{conectado ? 'Fechar' : 'Cancelar'}</Button>
                    {!conectado && (
                        <Button onClick={() => gerar(estado ? 'conectar' : 'criar')} disabled={gerando}>
                            {gerando ? 'Gerando…' : temQr ? 'Gerar outro código' : 'Gerar QR Code'}
                        </Button>
                    )}
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
                {erro && (
                    <Alert tone="danger" dismissible={false} title="Não deu para falar com o servidor" style={{ alignSelf: 'stretch' }}>
                        {erro}
                    </Alert>
                )}

                {!conectado && falhas.seguidas >= FALHAS_ATE_AVISAR && (
                    <Alert tone="warning" dismissible={false} title="O servidor do WhatsApp não está respondendo" style={{ alignSelf: 'stretch' }}>
                        As últimas {falhas.seguidas} consultas ficaram sem resposta ({falhas.motivo}). A tela continua
                        perguntando; se o celular já leu o código e nada muda aqui, avise quem administra a integração.
                    </Alert>
                )}

                {estado?.aviso && !conectado && (
                    <Alert tone="warning" dismissible={false} style={{ alignSelf: 'stretch' }}>
                        {estado.aviso}
                    </Alert>
                )}

                {conectado ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '18px 0' }}>
                        <span
                            aria-hidden
                            style={{
                                width: 52, height: 52, borderRadius: '50%', background: 'var(--success-soft)',
                                color: 'var(--success-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <Icon name="check" size={26} />
                        </span>
                        <strong style={{ fontSize: 15, fontWeight: 600 }}>Número conectado</strong>
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
                            {estado?.perfil ? `${estado.perfil} · ` : ''}
                            {estado?.numero ?? 'a sessão do WhatsApp está no ar'}
                            <br />
                            A Comunicação já envia e recebe por este canal.
                        </span>
                    </div>
                ) : (
                    <>
                        <ol style={{ alignSelf: 'stretch', margin: 0, paddingLeft: 20, fontSize: 12.5, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                            <li>No celular, abra o WhatsApp que vai atender.</li>
                            <li>Toque em <strong>Aparelhos conectados</strong> › <strong>Conectar aparelho</strong>.</li>
                            <li>Aponte a câmera para o código abaixo.</li>
                        </ol>

                        <div
                            style={{
                                width: 240, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                                background: '#ffffff', padding: 8,
                            }}
                        >
                            {temQr ? (
                                <img
                                    src={estado?.qrcode} alt="QR Code para conectar o WhatsApp"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            ) : (
                                <span style={{ fontSize: 12.5, color: 'var(--neutral-600)', textAlign: 'center', padding: 16 }}>
                                    {gerando
                                        ? 'Falando com o servidor…'
                                        : estado
                                            ? 'O código expirou. Gere outro — leva um segundo.'
                                            : 'Gere o código para conectar este número.'}
                                </span>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                            <Badge tone={estado?.status === 'conectando' ? 'info' : 'neutral'} dot>
                                {estado?.status === 'conectando' ? 'Esperando a leitura' : 'Sem sessão'}
                            </Badge>
                            {temQr && <span>o código vence em {restante}s</span>}
                            {estado?.instancia && <span>· instância <code>{estado.instancia}</code></span>}
                        </div>

                        {estado?.paircode && (
                            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                Ou digite o código <strong style={{ letterSpacing: '2px' }}>{estado.paircode}</strong> no
                                celular, em <em>Conectar com número de telefone</em>.
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
